import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

const FLESH = 0x3e6e62; // organic teal

/**
 * B3 Hive Mother parts — docs/02-bosses.md.
 * Organic lump body + shield bubble + kamikaze fly minions.
 */
export const bossHiveBody: ProceduralDef = {
  key: 'boss/hive/body',
  width: 208,
  height: 208,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    const R = c - 18;

    // Lumpy organic silhouette
    ctx.beginPath();
    const lumps = 9;
    for (let i = 0; i <= lumps; i++) {
      const a = (Math.PI * 2 * i) / lumps;
      const rr = R * (0.88 + 0.12 * Math.sin(i * 2.7));
      const x = c + Math.cos(a) * rr;
      const y = c + Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    fillOutlined(ctx, FLESH, 8);

    // Top highlight
    circle(ctx, c - R * 0.25, c - R * 0.25, R * 0.5);
    ctx.fillStyle = css(shade(FLESH, 1.18));
    ctx.fill();

    // Hive cells (egg pods) — orange glow like the screenshots' nest
    const cells: ReadonlyArray<readonly [number, number, number]> = [
      [-0.35, 0.15, 0.18], [0.1, -0.3, 0.22], [0.4, 0.25, 0.16],
      [-0.05, 0.42, 0.14], [0.42, -0.18, 0.12],
    ];
    for (const [nx, ny, nr] of cells) {
      circle(ctx, c + nx * R, c + ny * R, nr * R);
      fillOutlined(ctx, COLORS.dangerOrange, 4);
      circle(ctx, c + nx * R - 2, c + ny * R - 3, nr * R * 0.4);
      ctx.fillStyle = css(shade(COLORS.dangerOrange, 1.4));
      ctx.fill();
    }
    void h;
  },
};

/** Shield bubble overlay — visible while a minion wave is alive. */
export const bossHiveShield: ProceduralDef = {
  key: 'boss/hive/shield',
  width: 256,
  height: 256,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    const R = c - 8;
    const grad = ctx.createRadialGradient(c, c, R * 0.6, c, c, R);
    grad.addColorStop(0, 'rgba(155, 89, 208, 0.05)');
    grad.addColorStop(0.85, 'rgba(155, 89, 208, 0.25)');
    grad.addColorStop(1, 'rgba(195, 140, 240, 0.55)');
    circle(ctx, c, c, R);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = 'rgba(195, 140, 240, 0.9)';
    ctx.lineWidth = 3;
    ctx.stroke();
    // Hex shimmer marks
    ctx.fillStyle = 'rgba(220, 190, 250, 0.5)';
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i + 0.4;
      circle(ctx, c + Math.cos(a) * R * 0.92, c + Math.sin(a) * R * 0.92, 4);
      ctx.fill();
    }
    void h;
  },
};

/** Kamikaze hive fly — 2 wing frames. Nose points +X. */
export const bossHiveFly: ProceduralDef = {
  key: 'boss/hive/fly',
  width: 56,
  height: 56,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const wingY = frame === 0 ? 14 : 9;

    // Wings (translucent)
    ctx.fillStyle = 'rgba(220, 240, 235, 0.55)';
    ctx.beginPath();
    ctx.ellipse(c - 6, c - wingY, 12, 6, -0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(c - 6, c + wingY, 12, 6, 0.5, 0, Math.PI * 2);
    ctx.fill();

    // Body
    ctx.beginPath();
    ctx.ellipse(c, c, 16, 10, 0, 0, Math.PI * 2);
    fillOutlined(ctx, shade(FLESH, 0.85), 4);

    // Red eye toward the nose
    circle(ctx, c + 9, c, 5);
    fillOutlined(ctx, COLORS.dangerRed, 3);
    void h;
  },
};

/** Elite fly — bigger, armored, spiked crown. */
export const bossHiveElite: ProceduralDef = {
  key: 'boss/hive/fly-elite',
  width: 72,
  height: 72,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    // Spike crown
    ctx.fillStyle = css(0x2a2440);
    for (let i = 0; i < 7; i++) {
      const a = (Math.PI * 2 * i) / 7;
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a - 0.18) * 20, c + Math.sin(a - 0.18) * 20);
      ctx.lineTo(c + Math.cos(a) * 31, c + Math.sin(a) * 31);
      ctx.lineTo(c + Math.cos(a + 0.18) * 20, c + Math.sin(a + 0.18) * 20);
      ctx.closePath();
      ctx.fill();
    }
    // Body
    circle(ctx, c, c, 21);
    fillOutlined(ctx, shade(FLESH, 0.7), 5);
    circle(ctx, c - 5, c - 5, 8);
    ctx.fillStyle = css(shade(FLESH, 1.0));
    ctx.fill();
    // Eye
    circle(ctx, c + 8, c, 6.5);
    fillOutlined(ctx, COLORS.dangerOrange, 3);
    void h;
  },
};
